import type { DemoState } from "./demo";
import { initialWiki, roster, telemetry } from "../data/catalog";
export type EvidenceRecord = {
  id: string;
  title: string;
  kind: string;
  at: string;
  actor: string;
  revision: number;
  source: "Synthetic fixture" | "Recorded demo action";
  eventIds: string[];
  data: unknown;
};
export function recordIndex(s: DemoState): EvidenceRecord[] {
  const rows: EvidenceRecord[] = [];
  const add = (
    id: string,
    title: string,
    kind: string,
    data: unknown,
    eventIds: string[] = [],
    actor = "Seeded laboratory record",
    at = "2026-10-13T12:00:00Z",
    revision = 1,
  ) =>
    rows.push({
      id,
      title,
      kind,
      data,
      eventIds,
      actor,
      at,
      revision,
      source: eventIds.length ? "Recorded demo action" : "Synthetic fixture",
    });
  const event = (action: string) => s.audit.find((e) => e.action === action);
  add("CAL-184", "DIST-04 calibration record", "Equipment", {
    instrument: "DIST-04",
    serial: "OD-24189",
    method: "Reference thermometer comparison",
    performed: "2026-09-01",
    due: "2027-03-01",
    reference: "REF-T-019",
    points: [
      { referenceC: 100, indicatedC: 100.1 },
      { referenceC: 200, indicatedC: 200.1 },
      { referenceC: 350, indicatedC: 350.1 },
    ],
    toleranceC: 0.3,
    expandedUncertaintyC: 0.2,
    coverageFactor: 2,
    certificate: "CAL-CERT-184",
    certificateStatus:
      "Synthetic certificate dataset; no real external laboratory certificate attached",
    reviewedBy: "Maya Chen",
  });
  add("CRM-CERT-241", "Reference material lot record", "Reference material", {
    lot: "CRM-241",
    property: "D86 T90",
    assignedValue: 338.0,
    unit: "°C",
    expandedUncertainty: 0.4,
    coverageFactor: 2,
    expiry: "2027-01-31",
    supplier: "Fictional Reference Materials Lab",
    status: "Synthetic certificate; not a certified commercial material",
  });
  add("TRACE-884", "DIST-04 process trace", "Instrument telemetry", {
    instrument: "DIST-04",
    timezone: "America/Chicago",
    sampleInterval: "10-minute summaries plus three 4-minute warning windows",
    observations: telemetry,
    warning: "11:12",
    windowStart: "07:32",
    policy: "CTX-01 rev 4",
    threshold: 0.7,
    interpretation:
      "Possible instrument-related bias; no automatic conformity decision.",
  });
  add(
    "QC-611",
    "Pre-batch quality control",
    "QC",
    {
      instrument: "DIST-04",
      lot: "CRM-241",
      targetC: 338.0,
      resultC: 338.2,
      labControlSigmaC: 0.5,
      zScore: 0.4,
      actionLimitAbsZ: 3,
      result: "Accepted",
      analyst: "Aisha Patel",
      performed: "2026-10-13T12:00:00Z",
      policy: "Lab-defined demo control limits; not a method precision limit",
    },
    ["EVT-001"],
    "Aisha Patel",
  );
  add("COC-1941", "Sample receipt and custody", "Sample handling", {
    customer: "Northstar Fuels",
    order: "NS-1042",
    received: "2026-10-13T11:30:00Z",
    receivedBy: "Aisha Patel",
    condition: "Six sealed retained samples; seals intact",
    sampling: "Provided by customer; lab did not sample",
    aliquotStorage: "Cabinet F-02; sealed ambient storage",
    samples: s.samples
      .filter((x) => x.order === "NS-1042")
      .map((x) => ({
        sampleId: x.id,
        originalRunStart: x.time,
        originalResult: x.t90,
        unit: "°C",
        instrument: x.instrument,
        originalRecordId: "RUN-" + x.id,
      })),
  });
  add(
    "COMP-041",
    "D86 analyst authorization",
    "Personnel",
    roster.find((r) => r.evidence === "COMP-041"),
  );
  add(
    "SOP-D86-014-R7",
    "D86 work instruction · revision 7",
    "Controlled procedure",
    {
      ...initialWiki.find((w) => w.id === "CTX-03"),
      method:
        "ASTM D86 (contract method; edition must be confirmed for real work)",
      approvedBy: "Maya Chen",
      effective: "2026-09-01",
      documentState: "Baseline controlled demo copy",
    },
    [],
    "Maya Chen",
    "2026-09-01T12:00:00Z",
    7,
  );
  add(
    "CTX-01-R4",
    "Approved release rule at warning time",
    "Decision rule",
    {
      ...initialWiki[0],
      interpretation:
        "Baseline policy captured with the incident; later Knowledge Hub edits do not rewrite it.",
    },
    [],
    "Maya Chen",
    "2026-09-01T12:00:00Z",
    4,
  );
  const hold = event("Containment approved");
  if (hold)
    add(
      "HOLD-024",
      "Result containment approval",
      "Nonconforming work",
      {
        instrument: "DIST-04",
        reason: hold.detail,
        affected: s.samples
          .filter((x) => x.instrument === "DIST-04")
          .map((x) => x.id),
        currentDisposition: s.held ? "Held" : "Released after verification",
      },
      [hold.id],
      hold.actor,
      hold.at,
    );
  const investigation = event("Investigation evidence recorded");
  if (investigation)
    add(
      "WO-301",
      "Physical investigation observations",
      "Technical work",
      {
        observations: [
          {
            check: "Flask seating",
            result: "1.8 mm gap observed",
            evidence: "Inspection worksheet entry",
          },
          {
            check: "Reference probe comparison",
            result: "+0.1 °C",
            evidence: "CAL-184",
          },
          {
            check: "Preparation records",
            result: "Same lot and procedure",
            evidence: "COC-1941",
          },
        ],
        hypothesis: "FM-D86-001",
        conclusion: s.capa.rootCause ?? null,
        approval: s.capa.rootCause?.signedOffBy ?? null,
      },
      [investigation.id],
      investigation.actor,
      investigation.at,
    );
  const correction = event("Corrective work evidenced");
  if (correction)
    add(
      "WO-302",
      "Corrective action worksheet",
      "Technical work",
      {
        seating: "Flask reseated; visible gap removed",
        referenceCheckC: 0.1,
        crmT90C: 338.3,
        zScore: 0.6,
        procedure: "SOP-D86-014-R7",
        completedBy: correction.actor,
        planApprovedBy: s.capa.plan.approvedBy,
      },
      [correction.id],
      correction.actor,
      correction.at,
    );
  const prevention = event("Preventive work evidenced");
  if (prevention) {
    add(
      "WO-303",
      "Procedure revision and briefing",
      "Preventive action",
      {
        briefed: ["Luis Ortega", "Aisha Patel"],
        change: "Record seating verification before every D86 batch",
        newProcedure: "SOP-D86-014-R8",
      },
      [prevention.id],
      prevention.actor,
      prevention.at,
    );
    add(
      "SOP-D86-014-R8",
      "D86 work instruction · revision 8",
      "Controlled procedure",
      {
        ...s.wiki.find((w) => w.id === "CTX-03"),
        change: "Add mandatory seating inspection record",
        approvedUnder: s.capa.id,
      },
      [prevention.id],
      s.capa.plan.approvedBy || "Pending",
      prevention.at,
      8,
    );
  }
  add(
    "CAPA-026",
    "Formal CAPA record",
    "Quality management",
    s.capa,
    s.audit.filter((e) => e.record === "CAPA-026").map((e) => e.id),
    s.capa.owner,
    s.capa.updatedAt,
  );
  const recovery = event("Retest and return-to-service approved");
  if (recovery && s.verified) {
    add(
      "QC-612",
      "Recovery QC and equipment release",
      "QC",
      {
        instrument: "DIST-04",
        targetC: 338.0,
        resultC: 338.3,
        sigmaC: 0.5,
        zScore: 0.6,
        absZLimit: 3,
        alternateInstrument: "DIST-02",
        alternateQc: { resultC: 338.1, zScore: 0.2 },
        authorizedBy: recovery.actor,
      },
      [recovery.id],
      recovery.actor,
      recovery.at,
    );
    add(
      "RETEST-024",
      "Retest result lineage",
      "Technical records",
      {
        retainedAliquots: true,
        performedBy: "Aisha Patel",
        procedure: "SOP-D86-014-R7",
        results: s.samples
          .filter((x) => x.instrument === "DIST-04")
          .map((x, i) => ({
            sampleId: x.id,
            original: x.t90,
            retest: x.retest,
            unit: "°C",
            instrument: "DIST-02",
            runStart: ["12:30", "12:55", "13:20", "13:45", "14:10", "14:35"][i],
            originalPreserved: true,
            supersedes: "RUN-" + x.id,
          })),
      },
      [recovery.id],
      "Aisha Patel",
      recovery.at,
    );
  }
  const release = event("Customer release approved");
  if (release && s.delivered)
    add(
      "COA-1042",
      "Northstar test report · authorized draft",
      "Result report",
      {
        reportId: "COA-1042",
        revision: 1,
        lab: s.lab,
        labAddress: "100 Research Drive, Houston, TX · fictional demo location",
        customer: "Northstar Fuels · fictional demo customer",
        order: "NS-1042",
        receivedAt: "2026-10-13T11:30:00Z",
        testDate: "2026-10-13",
        method: "ASTM D86 · T90 only in this demonstration",
        sampling:
          "Customer-provided samples; results apply to samples as received",
        deviations:
          "Original DIST-04 values superseded by retained-aliquot retests on DIST-02",
        results: s.samples
          .filter((x) => x.order === "NS-1042")
          .map((x) => ({
            sampleId: x.id,
            t90C: x.retest,
            source: "RETEST-024",
          })),
        conformity:
          "No statement of conformity issued; customer decision rule and uncertainty review not supplied",
        authorizedBy: release.actor,
        authorizedAt: release.at,
        delivery:
          "Authorized for download; no customer transmission has occurred",
        limitation:
          "This excerpt is not a complete D86 certificate or an accredited report.",
      },
      [release.id],
      release.actor,
      release.at,
    );
  for (const ex of s.exports)
    add(
      ex.id,
      "Retained result-export snapshot",
      "Export",
      {
        ...ex,
        delivery: "Browser download only; no receipt from customer or LIMS",
      },
      s.audit.filter((e) => e.record === ex.id).map((e) => e.id),
      "Maya Chen",
      ex.at,
    );
  if (s.capa.effectiveness?.result === "passed")
    add(
      "EFF-026",
      "Effectiveness review",
      "CAPA effectiveness",
      {
        ...s.capa.effectiveness,
        mode: "Explicit 30-day simulation, not 30 days of observed production history",
      },
      s.audit.filter((e) => e.action === "WATCH PASSED").map((e) => e.id),
      "Effectiveness monitor",
      s.capa.effectiveness.until,
    );
  return rows;
}
export function evidenceChecklist(s: DemoState) {
  const records = recordIndex(s);
  const has = (id: string) => records.some((r) => r.id === id);
  return [
    {
      id: "E-01",
      clause: "6.4 / 6.5",
      title: "Equipment and reference traceability",
      refs: ["CAL-184", "CRM-CERT-241"],
      review:
        "Synthetic calibration and lot datasets; external certificate authenticity is not verified.",
    },
    {
      id: "E-02",
      clause: "7.7",
      title: "QC and instrument warning history",
      refs: ["QC-611", "TRACE-884"],
      review:
        "Lead time is to scheduled QC, not proven failure detection advantage.",
    },
    {
      id: "E-03",
      clause: "7.4 / 7.10",
      title: "Sample scope and containment",
      refs: ["COC-1941", "HOLD-024"],
      review: "Original sample identifiers and result values remain retained.",
    },
    {
      id: "E-04",
      clause: "7.5 / 8.7",
      title: "Investigation and independent root-cause review",
      refs: ["WO-301"],
      review: s.capa.rootCause?.signedOffBy
        ? "Root cause reviewed independently."
        : "Independent root-cause approval is outstanding.",
      gate: !!s.capa.rootCause?.signedOffBy,
    },
    {
      id: "E-05",
      clause: "8.7",
      title: "Approved corrective and preventive actions",
      refs: ["CAPA-026", "WO-302", "WO-303"],
      review: "Action records and plan approval must both exist.",
      gate: !!s.capa.plan.approvedBy && s.corrected,
    },
    {
      id: "E-06",
      clause: "6.2 / 8.3",
      title: "Competency and controlled procedure",
      refs: ["COMP-041", "SOP-D86-014-R7", "CTX-01-R4"],
      review:
        "Baseline document versions captured separately from current wiki content.",
    },
    {
      id: "E-07",
      clause: "7.5 / 7.7",
      title: "Recovery QC and retest lineage",
      refs: ["QC-612", "RETEST-024"],
      review:
        "Retests preserve originals and identify the alternate instrument.",
      gate:
        s.verified &&
        s.samples
          .filter((x) => x.instrument === "DIST-04")
          .every((x) => Number.isFinite(x.retest)),
    },
    {
      id: "E-08",
      clause: "7.8",
      title: "Authorized customer report",
      refs: ["COA-1042"],
      review:
        "Authorized excerpt only. Full method report, uncertainty and agreed decision rules need review.",
    },
    {
      id: "E-09",
      clause: "8.7",
      title: "Effectiveness follow-up",
      refs: ["EFF-026"],
      review:
        "Separate from customer release; requires the explicit 30-day simulation.",
    },
  ].map((e) => ({
    ...e,
    complete: e.refs.every(has) && e.gate !== false,
    missing: e.refs.filter((id) => !has(id)),
  }));
}
export async function evidenceEnvelope(s: DemoState) {
  const records = recordIndex(s);
  const checksums = await Promise.all(
    records.map(async (r) => {
      const bytes = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(JSON.stringify(r)),
      );
      return {
        id: r.id,
        sha256: Array.from(new Uint8Array(bytes))
          .map((n) => n.toString(16).padStart(2, "0"))
          .join(""),
      };
    }),
  );
  return {
    schema: "trucision.evidence.v2",
    demo: true,
    generatedAt: new Date().toISOString(),
    organization: "Trucision Laboratory Services",
    lab: s.lab,
    scope: "NS-1042 / DIST-04 / CAPA-026",
    coverage: evidenceChecklist(s),
    records,
    checksums,
    events: s.audit,
    previousPrototypeTiming: s.ops.legacyTiming??null,
    limitations: [
      "Synthetic records; not an accredited report or compliance certificate.",
      "Hashes verify payload consistency only, not authenticity or tamper-resistant storage.",
      "No authenticated electronic signatures, validated retention controls or live customer delivery.",
      "Any production use needs the applicable method edition, uncertainty and contract review.",
    ],
  };
}
